import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Container,
  Title,
  Text,
  Button,
  Group,
  Stack,
  Card,
  Center,
  Modal,
  Stepper,
  Alert,
  Image
} from '@mantine/core';
import { IconArrowLeft, IconId, IconCamera, IconCheck, IconQrcode } from '@tabler/icons-react';
import QRCode from 'qrcode';

const IdentityVerificationPage = () => {
  const [modalOpened, setModalOpened] = useState(true);
  const [step, setStep] = useState(0);
  const [qrCode, setQrCode] = useState('');
  const [verificationComplete, setVerificationComplete] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Generate QR code when modal opens
    const generateQR = async () => {
      const verificationData = JSON.stringify({
        userId: 'pending',
        action: 'verify-identity',
        timestamp: Date.now()
      });
      try {
        const qr = await QRCode.toDataURL(verificationData, {
          width: 200,
          margin: 2
        });
        setQrCode(qr);
      } catch (err) {
        console.error('QR generation failed:', err);
      }
    };
    if (modalOpened && step === 0) {
      generateQR();
    }
  }, [modalOpened, step]);

  const handleQRScanned = () => {
    setStep(1);
  };

  const handleFrontCapture = () => {
    setStep(2);
  };

  const handleBackCapture = () => {
    setStep(4);
  };

  const handleSelfieCapture = () => {
    setStep(5);
    setVerificationComplete(true);
  };

  const handleClose = () => {
    setModalOpened(false);
    navigate('/');
  };

  return (
    <Container size="sm" py="xl">
      <Stack gap="xl">
        <Group justify="space-between" align="center">
          <Button 
            component={Link} 
            to="/register" 
            variant="subtle" 
            leftSection={<IconArrowLeft size="1rem" />}
          >
            Back to Registration
          </Button>
        </Group>

        <Center>
          <Stack gap="md" align="center" maw={400}>
            <IconId size={48} color="var(--mantine-color-grape-6)" />
            <Title order={1} ta="center">
              Identity Verification
            </Title>
            <Text size="lg" ta="center" c="dimmed">
              Verify your identity to become a Pedi Princess
            </Text>
          </Stack>
        </Center>

        <Card shadow="md" padding="xl" radius="md" withBorder>
          <Stepper active={step} orientation="vertical">
            <Stepper.Step 
              label="Scan QR Code" 
              description="Scan this code with your phone"
              icon={<IconQrcode size={18} />}
            >
              <Stack align="center" gap="md" py="md">
                {qrCode && (
                  <Card shadow="sm" padding="md" withBorder>
                    <Image src={qrCode} alt="Verification QR Code" />
                  </Card>
                )}
                <Text size="sm" c="dimmed" ta="center">
                  Scan this QR code with your phone's camera
                </Text>
                <Button 
                  color="grape" 
                  onClick={handleQRScanned}
                >
                  I've Scanned the QR Code
                </Button>
              </Stack>
            </Stepper.Step>

            <Stepper.Step 
              label="Front of ID" 
              description="Take photo of front"
              icon={<IconCamera size={18} />}
            >
              <Stack align="center" gap="md" py="md">
                <IconCamera size={48} color="var(--mantine-color-grape-6)" />
                <Text ta="center">
                  Take a clear photo of the <strong>front</strong> of your state ID or driver's license
                </Text>
                <Alert color="blue" variant="light">
                  Make sure all text is readable and not blurred
                </Alert>
                <Button 
                  color="grape" 
                  onClick={handleFrontCapture}
                >
                  Capture Front of ID
                </Button>
              </Stack>
            </Stepper.Step>

            <Stepper.Step 
              label="Back of ID" 
              description="Take photo of back"
              icon={<IconCamera size={18} />}
            >
              <Stack align="center" gap="md" py="md">
                <IconCamera size={48} color="var(--mantine-color-grape-6)" />
                <Text ta="center">
                  Take a clear photo of the <strong>back</strong> of your state ID or driver's license
                </Text>
                <Alert color="blue" variant="light">
                  Make sure all text is readable and not blurred
                </Alert>
                <Button 
                  color="grape" 
                  onClick={handleBackCapture}
                >
                  Capture Back of ID
                </Button>
              </Stack>
            </Stepper.Step>

            <Stepper.Step 
              label="Selfie Verification" 
              description="Take a selfie to match your face"
              icon={<IconCamera size={18} />}
            >
              <Stack align="center" gap="md" py="md">
                <IconCamera size={48} color="var(--mantine-color-grape-6)" />
                <Text ta="center">
                  Take a <strong>selfie</strong> to verify your identity matches your ID
                </Text>
                <Alert color="blue" variant="light">
                  Make sure your face is clearly visible and well-lit
                </Alert>
                <Button 
                  color="grape" 
                  onClick={handleSelfieCapture}
                >
                  Take Selfie
                </Button>
              </Stack>
            </Stepper.Step>

            <Stepper.Completed>
              <Stack align="center" gap="md" py="xl">
                <IconCheck size={48} color="var(--mantine-color-green-6)" />
                <Title order={2} ta="center">
                  Verification Submitted!
                </Title>
                <Text ta="center" c="dimmed">
                  Your identity documents have been submitted for review.
                  We'll notify you once verification is complete.
                </Text>
                <Button 
                  color="grape" 
                  size="lg"
                  onClick={handleClose}
                  mt="md"
                >
                  Continue to Dashboard
                </Button>
              </Stack>
            </Stepper.Completed>
          </Stepper>
        </Card>

        <Text size="sm" c="dimmed" ta="center">
          Your information is encrypted and secure. 
          We only use it for verification purposes.
        </Text>
      </Stack>
    </Container>
  );
};

export default IdentityVerificationPage;